import { type DefaultMessagesResponse, createPlacement } from '@post-office/placement-common-v2';
import { type EligibleMessage } from '@post-office/shared-contracts/shared-request-middleware-context';
import { usePlacementApi } from '@post-office/use-placement-api';
import { useEffect, useState } from 'react';

type MessageTemplateContext = {
	title: string;
	description: string;
	flow: string;
	projectId: string;
};

export const { createFrontend, createBackend } = createPlacement({
	placementId: 'jira-project-space-connected',
	options: {
		choreographer: {
			isDefaultEnabled: false,
		},
	},
});

export const PlacementFrontend = createFrontend<{
	onView: () => void;
	onDismiss: () => void;
	onMessage: () => void;
	active: boolean;
	projectId: string;
}>((Messages) => ({ onView, onDismiss, onMessage, active, projectId }) => {
	const { data, error, isLoading } =
		usePlacementApi<DefaultMessagesResponse<MessageTemplateContext>>('/');

	const [messages, setMessages] = useState<
		DefaultMessagesResponse<MessageTemplateContext>['messages']
	>([]);

	if (error) {
		throw error;
	}

	useEffect(() => {
		if (!isLoading && data.messages.length !== 0) {
			const filteredMessages = data.messages.filter(
				(message: EligibleMessage & MessageTemplateContext) =>
					`${message.projectId}` === `${projectId}`,
			);
			if (filteredMessages.length !== 0) {
				setMessages(filteredMessages);
				onMessage();
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data, isLoading, projectId]);

	const handleDismiss = (messageInstanceId: string) => {
		setMessages(messages.filter((message) => messageInstanceId !== message.messageInstanceId));
		onDismiss();
	};

	const handleView = (messageInstanceId: string) => {
		setMessages(messages.filter((message) => messageInstanceId !== message.messageInstanceId));
		onView();
	};

	return (
		<>
			{messages.map((message) => (
				<Messages.render
					key={message.messageInstanceId}
					onView={() => handleView(message.messageInstanceId)}
					onDismiss={() => handleDismiss(message.messageInstanceId)}
					active={active}
					{...message}
				/>
			))}
		</>
	);
});

export const PlacementBackend = createBackend();
